import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, TrendingUp, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const SEVERITY_STYLES = {
  low: "bg-elevated text-textmuted border-seam",
  medium: "bg-champagne/10 text-champagne border-champagne/30",
  high: "bg-[#7a4a30]/20 text-[#E5B493] border-[#7a4a30]/50",
  critical: "bg-[#5C2B29]/30 text-[#E5A09E] border-[#5C2B29]/50",
};

const KpiRisk = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("enterprise.manage");
  const canManageRisks = hasPermission("risks.manage");
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState("");
  const [kpis, setKpis] = useState([]);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/enterprise/entities").then((r) => {
      setEntities(r.data);
      if (r.data.length > 0) setEntityId(r.data[0].id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const [k, r] = await Promise.all([
        api.get("/kpis", { params: { entity_id: entityId } }),
        api.get("/risks", { params: { entity_id: entityId } }),
      ]);
      setKpis(k.data);
      setRisks(r.data);
    } catch {
      toast.error("Could not load");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const addKpi = async () => {
    const name = window.prompt("KPI name:");
    if (!name) return;
    const unit = window.prompt("Unit (e.g. $, %, calls):", "") || "";
    const target = parseFloat(window.prompt("Target value (optional):", "") || "");
    try {
      await api.post("/kpis", { entity_id: entityId, name, unit, target: isNaN(target) ? null : target });
      toast.success("KPI added");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const recordValue = async (kpi) => {
    const raw = window.prompt(`New value for ${kpi.name}:`, "");
    if (!raw) return;
    const value = parseFloat(raw);
    if (isNaN(value)) return toast.error("Not a number");
    try {
      await api.post(`/kpis/${kpi.id}/values`, { value });
      toast.success("Recorded");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const addRisk = async () => {
    const title = window.prompt("Risk title:");
    if (!title) return;
    const severity = window.prompt("Severity (low|medium|high|critical):", "medium") || "medium";
    const category = window.prompt("Category:", "operational") || "operational";
    try {
      await api.post("/risks", { entity_id: entityId, title, severity, category });
      toast.success("Risk added");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const updateRisk = async (risk, patch) => {
    try {
      await api.patch(`/risks/${risk.id}`, patch);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <Layout>
      <section className="mb-5">
        <p className="label-eyebrow">Measurement & Exposure</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">KPIs & Risks</h1>
      </section>

      <div className="flex gap-2 items-center mb-5">
        <label className="label-eyebrow">Business</label>
        <select value={entityId} onChange={(e) => setEntityId(e.target.value)} className="bg-obsidian border border-seam rounded-md px-3 py-2 text-sm">
          {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>

      <Tabs defaultValue="kpis">
        <TabsList className="bg-surface border border-seam p-1 rounded-md">
          <TabsTrigger value="kpis" className="data-[state=active]:bg-elevated data-[state=active]:text-champagne text-textmuted">
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> KPIs
          </TabsTrigger>
          <TabsTrigger value="risks" className="data-[state=active]:bg-elevated data-[state=active]:text-champagne text-textmuted">
            <ShieldAlert className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Risks
          </TabsTrigger>
        </TabsList>

        <TabsContent value="kpis" className="mt-4">
          {canManage && (
            <button onClick={addKpi} data-testid="add-kpi-btn" className="mb-3 inline-flex items-center gap-2 text-sm border border-champagne/40 text-champagne px-3 py-1.5 rounded hover:bg-champagne/10">
              <Plus className="w-3.5 h-3.5" /> Add KPI
            </button>
          )}
          {loading ? <Loader2 className="w-6 h-6 text-champagne animate-spin mx-auto" /> : kpis.length === 0 ? (
            <p className="text-textmuted text-sm">No KPIs defined.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kpis.map((k) => (
                <div key={k.id} data-testid={`kpi-${k.id}`} className="regal-card p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="label-eyebrow">{k.category}</p>
                      <h3 className="font-heading text-xl mt-1">{k.name}</h3>
                    </div>
                    {canManage && (
                      <button onClick={() => recordValue(k)} className="text-xs text-champagne hover:underline">+ Value</button>
                    )}
                  </div>
                  <p className="font-sans text-3xl font-light mt-3">
                    {k.latest ? k.latest.value : "—"}
                    <span className="text-sm text-textmuted ml-1">{k.unit}</span>
                  </p>
                  {k.target != null && (
                    <p className="text-[11px] text-textmuted mt-1">Target: {k.target} {k.unit}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="risks" className="mt-4">
          {canManageRisks && (
            <button onClick={addRisk} data-testid="add-risk-btn" className="mb-3 inline-flex items-center gap-2 text-sm border border-champagne/40 text-champagne px-3 py-1.5 rounded hover:bg-champagne/10">
              <Plus className="w-3.5 h-3.5" /> Add risk
            </button>
          )}
          {loading ? <Loader2 className="w-6 h-6 text-champagne animate-spin mx-auto" /> : risks.length === 0 ? (
            <p className="text-textmuted text-sm">No risks recorded.</p>
          ) : (
            <div className="regal-card overflow-hidden">
              <div className="divide-y divide-seam">
                {risks.map((r) => (
                  <div key={r.id} data-testid={`risk-${r.id}`} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${SEVERITY_STYLES[r.severity] || SEVERITY_STYLES.medium}`}>
                            {r.severity}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-champagne">{r.category?.replace(/_/g, " ")}</span>
                          <span className="text-[10px] uppercase tracking-wider text-textmuted">· {r.status}</span>
                        </div>
                        <h3 className="font-heading text-lg mt-1">{r.title}</h3>
                        {r.description && <p className="text-xs text-textmuted mt-1">{r.description}</p>}
                      </div>
                      {canManageRisks && (
                        <select
                          value={r.status}
                          onChange={(e) => updateRisk(r, { status: e.target.value })}
                          className="bg-obsidian border border-seam rounded-md px-2 py-1 text-xs text-textmuted focus:outline-none focus:border-champagne"
                        >
                          <option value="open">open</option>
                          <option value="mitigating">mitigating</option>
                          <option value="closed">closed</option>
                        </select>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Layout>
  );
};

export default KpiRisk;
